/**
 * Notification settings and a lead's SMS thread.
 * Props: NotificationSettings({ api, toast }), LeadSmsThread({ api, toast, leadId, timezone }).
 */
const { useEffect, useMemo, useRef, useState } = React;

const EVENTS = [
  ['hot_lead', 'A lead is interested or hot'],
  ['callback_requested', 'Someone asked for a callback'],
  ['agent_requested_missed', 'A caller asked for a person and nobody picked up'],
  ['follow_up_due', 'A follow-up was created from the call'],
  ['usage_80', 'Included minutes reach 80%'],
  ['usage_100', 'Included minutes are used up']
];
const CONDITIONS = [
  ['interested', 'Only after an interested call'],
  ['callback', 'Only when they asked for a callback'],
  ['any_connected', 'After any connected call']
];
const VARIABLES = [
  ['{first_name}', 'First name'],
  ['{company}', 'Company'],
  ['{agent_name}', 'Agent name'],
  ['{business_name}', 'Business'],
  ['{booking_link}', 'Booking link']
];
const SAMPLE = {
  first_name: 'Jordan',
  company: 'Northwind Dental',
  agent_name: 'Alex',
  business_name: 'Northwind Dental',
  booking_link: 'https://book.example/jordan'
};
const A2P_NOTE = 'US texting requires A2P 10DLC registration of the number you send from. Unregistered numbers are filtered or blocked. Reply STOP opts the person out, and that opt-out is stored on your Do Not Call list.';
const GSM_BASIC = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = new Set(['^', '{', '}', '\\', '[', '~', ']', '|', '€']);

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function withStop(text) {
  const value = String(text || '').trim();
  if (/reply\s+stop\s+to\s+opt\s+out/i.test(value)) return value;
  return value ? value + '\nReply STOP to opt out.' : 'Reply STOP to opt out.';
}

function previewTemplate(template) {
  const source = String(template || '').trim() || 'Hi {first_name}, thanks for speaking with {agent_name} at {business_name}. {booking_link}';
  const filled = source.replace(/\{(first_name|company|agent_name|business_name|booking_link)\}/g, function (_all, key) {
    return SAMPLE[key] || '';
  });
  return withStop(filled.replace(/[ \t]{2,}/g, ' ').trim());
}

function smsLength(text) {
  const value = String(text || '');
  let units = 0;
  let gsm = true;
  for (const ch of value) {
    if (GSM_BASIC.indexOf(ch) >= 0) units += 1;
    else if (GSM_EXT.has(ch)) units += 2;
    else { gsm = false; break; }
  }
  if (!gsm) {
    const len = Array.from(value).length;
    const segments = len === 0 ? 0 : (len <= 70 ? 1 : Math.ceil(len / 67));
    return { encoding: 'Unicode', characters: len, segments };
  }
  const segments = units === 0 ? 0 : (units <= 160 ? 1 : Math.ceil(units / 153));
  return { encoding: 'GSM-7', characters: units, segments };
}

function formatWhen(iso, timeZone) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  } catch (_) {
    return date.toLocaleString();
  }
}

export function NotificationSettings({ api, toast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailReady, setEmailReady] = useState(false);
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [slackUrl, setSlackUrl] = useState('');
  const [slackMask, setSlackMask] = useState('');
  const [slackSet, setSlackSet] = useState(false);
  const [events, setEvents] = useState([]);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [condition, setCondition] = useState('interested');
  const [template, setTemplate] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [quietStart, setQuietStart] = useState('');
  const [quietEnd, setQuietEnd] = useState('');
  const [note, setNote] = useState(A2P_NOTE);
  const [log, setLog] = useState([]);
  const templateRef = useRef(null);

  function notify(message, type) { if (toast) toast(message, type); }

  function applySettings(settings) {
    const data = settings || {};
    setEmailEnabled(!!data.email_enabled);
    setEmailTo(data.email_to || '');
    setEmailReady(!!data.email_delivery_configured);
    setSlackEnabled(!!data.slack_enabled);
    setSlackMask(data.slack_webhook_masked || '');
    setSlackSet(!!data.slack_webhook_set);
    setSlackUrl('');
    setEvents(Array.isArray(data.notify_on) ? data.notify_on : []);
    setSmsEnabled(!!data.sms_followup_enabled);
    setCondition(data.sms_only_when || 'interested');
    setTemplate(data.sms_template || '');
    setFromNumber(data.sms_from_number || '');
    setNumbers(Array.isArray(data.from_numbers) ? data.from_numbers : []);
    setQuietStart(data.quiet_hours && data.quiet_hours.start ? data.quiet_hours.start : '');
    setQuietEnd(data.quiet_hours && data.quiet_hours.end ? data.quiet_hours.end : '');
    if (data.compliance_note) setNote(data.compliance_note);
  }

  function load() {
    setLoading(true);
    setError('');
    Promise.all([api('GET', '/api/notifications/settings'), api('GET', '/api/notifications/log?limit=20')])
      .then(function (results) {
        applySettings(results[0] && results[0].settings);
        setLog((results[1] && results[1].items) || []);
      })
      .catch(function (err) { setError(errorMessage(err, 'Could not load notification settings.')); })
      .finally(function () { setLoading(false); });
  }

  useEffect(function () { load(); }, []);

  const preview = useMemo(function () { return previewTemplate(template); }, [template]);
  const segments = smsLength(preview);
  const fromOptions = numbers.slice();
  if (fromNumber && !fromOptions.some(function (row) { return row.e164 === fromNumber; })) {
    fromOptions.unshift({ e164: fromNumber, role: 'sms', is_primary: false });
  }

  function toggleEvent(id) {
    setEvents(function (current) {
      return current.indexOf(id) >= 0 ? current.filter(function (item) { return item !== id; }) : current.concat([id]);
    });
  }

  function insertVariable(token) {
    const el = templateRef.current;
    const value = template;
    const focused = el && typeof document !== 'undefined' && document.activeElement === el;
    const start = focused && el.selectionStart != null ? el.selectionStart : value.length;
    const end = focused && el.selectionEnd != null ? el.selectionEnd : value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    setTemplate(next);
    if (el) {
      requestAnimationFrame(function () {
        el.focus();
        const caret = start + token.length;
        el.setSelectionRange(caret, caret);
      });
    }
  }

  async function save(extra) {
    setSaving(true);
    setError('');
    const payload = Object.assign({
      email_enabled: emailEnabled,
      email_to: emailTo,
      slack_enabled: slackEnabled,
      notify_on: events,
      sms_followup_enabled: smsEnabled,
      sms_only_when: condition,
      sms_template: template,
      sms_from_number: fromNumber,
      quiet_hours: quietStart || quietEnd ? { start: quietStart, end: quietEnd } : null
    }, extra || {});
    if (slackUrl.trim() && !(extra && Object.prototype.hasOwnProperty.call(extra, 'slack_webhook_url'))) {
      payload.slack_webhook_url = slackUrl.trim();
    }
    try {
      const result = await api('PUT', '/api/notifications/settings', payload);
      applySettings(result && result.settings);
      notify('Notification settings saved');
      return true;
    } catch (err) {
      const message = errorMessage(err, 'Could not save notification settings.');
      setError(message);
      notify(message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function sendTest(channel) {
    setSaving(true);
    try {
      await api('POST', '/api/notifications/test', { channel: channel });
      notify(channel === 'slack' ? 'Slack test sent' : 'Email test sent');
      const fresh = await api('GET', '/api/notifications/log?limit=20');
      setLog((fresh && fresh.items) || []);
    } catch (err) {
      notify(errorMessage(err, 'The test could not be sent.'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="ntf">
      <div>
        <h2 className="ntf-title">Notifications</h2>
        <p className="ntf-sub">Email or Slack a rep when a call needs a person, and text the lead after a connected call.</p>
      </div>
      {error ? <p className="ntf-error">{error}</p> : null}
      {loading ? <p className="ntf-empty">Loading…</p> : (
        <React.Fragment>
          <div className="ntf-card">
            <h3>Email</h3>
            <div className="ntf-grid">
              <label className="ntf-check">
                <input type="checkbox" checked={emailEnabled} onChange={function (e) { setEmailEnabled(e.target.checked); }} />
                <span>Email the people below</span>
              </label>
              <label className="ntf-label">
                Recipients
                <input className="ntf-input" value={emailTo} onChange={function (e) { setEmailTo(e.target.value); }} placeholder="ada@northwind.example, sam@northwind.example" />
              </label>
              {emailReady ? <p className="ntf-help">Email delivery is configured on the server.</p> : <p className="ntf-help">Email sending needs RESEND_API_KEY and NOTIFY_FROM_EMAIL on the server.</p>}
            </div>
          </div>
          <div className="ntf-card">
            <h3>Slack</h3>
            <div className="ntf-grid">
              <label className="ntf-check">
                <input type="checkbox" checked={slackEnabled} onChange={function (e) { setSlackEnabled(e.target.checked); }} />
                <span>Post to a Slack channel</span>
              </label>
              <label className="ntf-label">
                Incoming webhook
                <input className="ntf-input" value={slackUrl} onChange={function (e) { setSlackUrl(e.target.value); }} placeholder={slackMask || 'https://hooks.slack.com/services/…'} autoComplete="off" />
              </label>
              {slackSet ? <p className="ntf-help">Saved webhook {slackMask}. Leave the box blank to keep it.</p> : <p className="ntf-help">Paste a webhook that starts with https://hooks.slack.com/services/.</p>}
              {slackSet ? <button type="button" className="ntf-btn" disabled={saving} onClick={function () { save({ slack_webhook_url: '', slack_enabled: false }); }}>Remove Slack webhook</button> : null}
            </div>
          </div>
          <div className="ntf-card">
            <h3>When to alert</h3>
            <div className="ntf-checks">
              {EVENTS.map(function (item) {
                return (
                  <label className="ntf-check" key={item[0]}>
                    <input type="checkbox" checked={events.indexOf(item[0]) >= 0} onChange={function () { toggleEvent(item[0]); }} />
                    <span>{item[1]}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="ntf-card">
            <h3>Text the lead after a call</h3>
            <div className="ntf-grid">
              <label className="ntf-check">
                <input type="checkbox" checked={smsEnabled} onChange={function (e) { setSmsEnabled(e.target.checked); }} />
                <span>Send a follow-up text after a connected call</span>
              </label>
              <label className="ntf-label">
                Send when
                <select className="ntf-select" value={condition} onChange={function (e) { setCondition(e.target.value); }}>
                  {CONDITIONS.map(function (item) { return <option key={item[0]} value={item[0]}>{item[1]}</option>; })}
                </select>
              </label>
              <label className="ntf-label">
                Sending number
                <select className="ntf-select" value={fromNumber} onChange={function (e) { setFromNumber(e.target.value); }}>
                  <option value="">Workspace outbound number</option>
                  {fromOptions.map(function (row) {
                    return <option key={row.e164} value={row.e164}>{row.e164}{row.is_primary ? ' · primary' : ''}</option>;
                  })}
                </select>
              </label>
              <div className="ntf-label">
                Message
                <div className="ntf-chips">
                  {VARIABLES.map(function (item) {
                    return <button type="button" className="ntf-chip" key={item[0]} onClick={function () { insertVariable(item[0]); }}>{item[1]}</button>;
                  })}
                </div>
                <textarea ref={templateRef} className="ntf-textarea" value={template} maxLength={600} onChange={function (e) { setTemplate(e.target.value); }} placeholder="Hi {first_name}, thanks for speaking with {agent_name} at {business_name}." />
              </div>
              <div>
                <p className="ntf-help">Preview</p>
                <div className="ntf-preview">{preview}</div>
                <p className="ntf-meta">{segments.characters} characters · {segments.segments} segment{segments.segments === 1 ? '' : 's'} · {segments.encoding}</p>
              </div>
              <div className="ntf-row">
                <label className="ntf-label">
                  Earliest local time
                  <input className="ntf-input" type="time" value={quietStart} onChange={function (e) { setQuietStart(e.target.value); }} />
                </label>
                <label className="ntf-label">
                  Latest local time
                  <input className="ntf-input" type="time" value={quietEnd} onChange={function (e) { setQuietEnd(e.target.value); }} />
                </label>
              </div>
              <p className="ntf-help">Texts send only inside this window in the lead’s local time, and only from 8:00 AM to 9:00 PM. Leave both blank to use that full window.</p>
              <p className="ntf-a2p">{note}</p>
            </div>
          </div>
          <div className="ntf-actions">
            <button type="button" className="ntf-btn" disabled={saving} onClick={function () { save(); }}>Save</button>
            <button type="button" className="ntf-btn" disabled={saving} onClick={function () { sendTest('email'); }}>Send test email</button>
            <button type="button" className="ntf-btn" disabled={saving} onClick={function () { sendTest('slack'); }}>Send test Slack</button>
          </div>
          <div className="ntf-card">
            <h3>Recent alerts</h3>
            {log.length ? (
              <table className="ntf-table">
                <thead>
                  <tr><th>When</th><th>Alert</th><th>Channel</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {log.map(function (row) {
                    return (
                      <tr key={row.id}>
                        <td>{formatWhen(row.created_at)}</td>
                        <td>{row.kind}{row.target_masked ? ' · ' + row.target_masked : ''}</td>
                        <td>{row.channel}</td>
                        <td className={'ntf-status ntf-status--' + row.status}>{row.status}{row.error ? ' · ' + row.error : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : <p className="ntf-empty">No alerts yet.</p>}
          </div>
        </React.Fragment>
      )}
    </section>
  );
}

export function LeadSmsThread({ api, toast, leadId, timezone }) {
  const [messages, setMessages] = useState([]);
  const [optedOut, setOptedOut] = useState(false);
  const [smsOptOut, setSmsOptOut] = useState(false);
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  function load() {
    if (!leadId) return;
    setLoading(true);
    api('GET', '/api/leads/' + encodeURIComponent(leadId) + '/sms')
      .then(function (data) {
        setMessages((data && data.messages) || []);
        setOptedOut(!!(data && data.opted_out));
        setSmsOptOut(!!(data && data.sms_opt_out));
        setError('');
      })
      .catch(function (err) { setError(errorMessage(err, 'Could not load texts.')); })
      .finally(function () { setLoading(false); });
  }

  useEffect(function () { load(); }, [leadId]);

  async function send() {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      await api('POST', '/api/leads/' + encodeURIComponent(leadId) + '/sms', { body: text });
      setBody('');
      if (toast) toast('Text sent');
      load();
    } catch (err) {
      const message = errorMessage(err, 'The text could not be sent.');
      setError(message);
      if (toast) toast(message, 'error');
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="sms-thread">
      <h3>Texts</h3>
      {smsOptOut ? <p className="sms-banner">This person texted STOP. Texts stay off until they text START.</p> : null}
      {optedOut && !smsOptOut ? <p className="sms-banner">This number is on the Do Not Call list.</p> : null}
      {error ? <p className="ntf-error">{error}</p> : null}
      {loading ? <p className="ntf-empty">Loading…</p> : (
        messages.length ? (
          <div className="sms-log">
            {messages.map(function (row) {
              const outbound = row.direction === 'outbound';
              return (
                <div key={row.id} className={'sms-bubble ' + (outbound ? 'sms-bubble--out' : 'sms-bubble--in')}>
                  <div>{row.body}</div>
                  <time dateTime={row.created_at}>{formatWhen(row.created_at, timezone)} · {row.status || row.direction}</time>
                </div>
              );
            })}
          </div>
        ) : <p className="ntf-empty">No texts with this lead yet.</p>
      )}
      <div className="sms-compose">
        <textarea aria-label="Text message" value={body} disabled={optedOut || sending} maxLength={1000} onChange={function (e) { setBody(e.target.value); }} placeholder={optedOut ? 'Texting is off for this lead' : 'Write a text. Reply STOP is added for you.'} />
        <button type="button" className="sms-send" disabled={optedOut || sending || !body.trim()} onClick={send}>Send</button>
      </div>
    </section>
  );
}
